/**
 * /customer/claim-stamp/status/:claimId — Steps 4-6 (Phase R3).
 *
 * Mirrors mobile `(customer)/claim-stamp/status/[claimId].tsx` —
 * one screen, three variants driven by `status`:
 *   • pending_owner_review → Pending (clock hero + timeline + 10 s polling)
 *   • approved             → Approved (green check + "+1 Stamp Awarded" + "View My Stamps")
 *   • rejected             → Rejected (red X + reason verbatim + "Try Again")
 *
 * NON-NEGOTIABLE #1: "+1 Stamp Awarded" copy renders ONLY when
 * status === "approved". AI never auto-awards; only owner approve
 * mints the stamp (R1 contract).
 */
import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, Clock, CheckCircle2, XCircle, Hourglass, Ribbon,
  RefreshCcw, Loader2,
} from 'lucide-react';

import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { formatRupiah, rejectionReasonLabel } from '@/lib/claimErrorUI';

const POLL_INTERVAL_MS = 10_000;

export default function CustomerClaimStampStatusPage() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const [claim, setClaim] = useState(null);
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    if (!token || !claimId) return;
    try {
      const row = await api.customerGetReceiptClaim(token, claimId);
      setClaim(row);
      if (!brand) {
        try {
          const b = await api.customerBrand(token, row.brand_id);
          setBrand(b);
        } catch { /* best-effort */ }
      }
    } catch { /* keep stale */ } finally { setLoading(false); }
  }, [brand, claimId, token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!claim) return undefined;
    if (claim.status === 'approved' || claim.status === 'rejected') {
      if (timerRef.current) clearInterval(timerRef.current);
      return undefined;
    }
    timerRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [claim, load]);

  if (loading && !claim) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3">
        <Loader2 size={32} className="text-gold animate-spin" />
      </div>
    );
  }
  if (!claim) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-sm text-white/60">
        Claim not found.
      </div>
    );
  }

  const variant =
    claim.status === 'approved'
      ? 'approved'
      : claim.status === 'rejected'
        ? 'rejected'
        : 'pending';
  const glowClass =
    variant === 'approved'
      ? 'from-emerald-500/15'
      : variant === 'rejected'
        ? 'from-red-500/15'
        : 'from-gold/15';

  return (
    <div className="relative" data-testid={`claim-status-${variant}`}>
      <div className={`pointer-events-none absolute inset-x-0 top-0 h-96 bg-gradient-to-b ${glowClass} to-transparent -z-10`} aria-hidden="true" />

      <button
        type="button"
        onClick={() => navigate('/customer/claim-stamp/history')}
        className="inline-flex items-center gap-1 text-sm text-white/55 hover:text-white"
        data-testid="claim-status-back"
      >
        <ChevronLeft size={14} /> Back
      </button>

      <div className="mt-4 max-w-2xl mx-auto">
        {variant === 'pending' && <PendingHero />}
        {variant === 'approved' && <ApprovedHero brandName={brand?.name} />}
        {variant === 'rejected' && (
          <RejectedHero reason={claim.owner_decision?.reason} />
        )}

        <ReceiptDetailsCard claim={claim} brandName={brand?.name} />

        {variant === 'pending' && <PendingTimeline />}

        {variant === 'rejected' && claim.owner_decision ? (
          <div
            className="mt-5 rounded-3xl border border-red-500/35 bg-red-500/10 p-5"
            data-testid="claim-status-reason"
          >
            <div className="text-[10px] uppercase tracking-widest text-red-300 font-semibold">
              Reason
            </div>
            <div className="font-semibold text-white mt-1.5 text-base">
              {rejectionReasonLabel(claim.owner_decision.reason)}
            </div>
            {claim.owner_decision.notes ? (
              <p className="text-sm text-white/70 italic mt-2">
                &ldquo;{claim.owner_decision.notes}&rdquo;
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Variant CTAs */}
        <div className="mt-6 space-y-3">
          {variant === 'approved' ? (
            <button
              type="button"
              onClick={() => navigate('/customer/cards')}
              data-testid="claim-status-view-stamps"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-2xl px-5 py-3.5 text-sm hover:bg-gold-light transition-colors shadow-glow-gold-sm min-h-[48px]"
            >
              <Ribbon size={16} /> View My Stamps
            </button>
          ) : null}
          {variant === 'rejected' ? (
            <button
              type="button"
              onClick={() => navigate(`/customer/claim-stamp/${claim.brand_id}`)}
              data-testid="claim-status-try-again"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-2xl px-5 py-3.5 text-sm hover:bg-gold-light transition-colors shadow-glow-gold-sm min-h-[48px]"
            >
              <RefreshCcw size={16} /> Try Again with Another Receipt
            </button>
          ) : null}
          {variant === 'pending' ? (
            <button
              type="button"
              onClick={() => navigate('/customer/home')}
              data-testid="claim-status-back-home"
              className="w-full inline-flex items-center justify-center gap-2 bg-transparent border border-gold/40 text-gold font-semibold rounded-2xl px-5 py-3 text-sm hover:bg-gold/10 transition-colors min-h-[48px]"
            >
              Back to Home
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Variant heroes
// ---------------------------------------------------------------------------
function PendingHero() {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="w-32 h-32 rounded-full bg-gold/10 border border-gold/30 grid place-items-center">
        <Clock size={56} className="text-gold" />
      </div>
      <h1 className="font-display text-3xl font-bold tracking-tight mt-5">
        Pending Verification
      </h1>
      <p className="text-sm text-white/65 mt-2.5 max-w-md leading-relaxed">
        Your receipt has been submitted. The brand will verify your receipt
        before awarding your stamp.
      </p>
    </div>
  );
}

function ApprovedHero({ brandName }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="w-32 h-32 rounded-full bg-emerald-500/10 border border-emerald-500/35 grid place-items-center">
        <CheckCircle2 size={64} className="text-emerald-400" />
      </div>
      <h1 className="font-display text-3xl font-bold tracking-tight mt-5">
        Receipt Approved
      </h1>
      <p className="text-sm text-white/65 mt-2.5 max-w-md leading-relaxed">
        You&apos;ve earned 1 stamp
        {brandName ? ` — thank you for dining at ${brandName}.` : '.'}
      </p>
      {/* NON-NEGOTIABLE #1: this badge renders ONLY in approved variant. */}
      <div
        className="mt-4 inline-flex items-center gap-2 bg-gold text-black font-bold rounded-full px-5 py-2.5 shadow-glow-gold-sm"
        data-testid="claim-status-stamp-awarded"
      >
        <Ribbon size={16} />
        <span className="text-sm">+1 Stamp Awarded</span>
      </div>
    </div>
  );
}

function RejectedHero({ reason }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="w-32 h-32 rounded-full bg-red-500/10 border border-red-500/35 grid place-items-center">
        <XCircle size={64} className="text-red-400" />
      </div>
      <h1 className="font-display text-3xl font-bold tracking-tight mt-5">
        Receipt Rejected
      </h1>
      <p className="text-sm text-white/65 mt-2.5 max-w-md leading-relaxed">
        {reason === 'duplicate'
          ? 'This receipt was already submitted and approved.'
          : 'This receipt could not be verified.'}
      </p>
    </div>
  );
}

function PendingTimeline() {
  return (
    <div
      className="mt-5 rounded-3xl border border-white/8 bg-[#15151B] p-5"
      data-testid="claim-status-timeline"
    >
      <TimelineRow
        icon={<CheckCircle2 size={18} className="text-emerald-400" />}
        title="Receipt submitted"
        subtitle="Your photo has been uploaded"
        state="complete"
      />
      <TimelineBar complete />
      <TimelineRow
        icon={<Hourglass size={18} className="text-gold" />}
        title="Under review"
        subtitle="The brand is verifying your receipt"
        state="current"
      />
      <TimelineBar complete={false} />
      <TimelineRow
        icon={<Ribbon size={18} className="text-white/40" />}
        title="Stamp will be awarded"
        subtitle="You'll receive a notification"
        state="pending"
      />
    </div>
  );
}

function TimelineRow({ icon, title, subtitle, state }) {
  const dotCls =
    state === 'complete'
      ? 'bg-emerald-500/15 border-emerald-500/35'
      : state === 'current'
        ? 'bg-gold/15 border-gold/35'
        : 'bg-white/5 border-white/10';
  const titleCls =
    state === 'pending' ? 'text-white/55' : 'text-white';
  return (
    <div className="flex items-start gap-3.5">
      <div className={`w-10 h-10 rounded-full border grid place-items-center shrink-0 ${dotCls}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${titleCls}`}>{title}</div>
        <div className="text-xs text-white/55 mt-0.5">{subtitle}</div>
      </div>
    </div>
  );
}

function TimelineBar({ complete }) {
  return (
    <div className="pl-5 py-1">
      <div className={`w-0.5 h-5 ${complete ? 'bg-emerald-500/35' : 'bg-white/10'}`} />
    </div>
  );
}

function ReceiptDetailsCard({ claim, brandName }) {
  const ai = claim.ai_extraction;
  const submitted = new Date(claim.created_at).toLocaleString('id-ID', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <div
      className="mt-5 rounded-3xl border border-white/8 bg-[#15151B] p-5"
      data-testid="claim-status-details"
    >
      <h3 className="font-display text-base font-semibold mb-3">
        Receipt Details
      </h3>
      <DetailRow label="Brand" value={brandName ?? ai?.brand_name ?? '—'} />
      <DetailRow label="Date" value={ai?.date ?? '—'} />
      <DetailRow label="Receipt No." value={ai?.receipt_no ?? '—'} />
      <DetailRow
        label="Total"
        value={ai?.total_amount != null ? formatRupiah(ai.total_amount) : '—'}
      />
      <DetailRow label="Submitted" value={submitted} />
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-b-0">
      <span className="text-sm text-white/60">{label}</span>
      <span className="text-sm font-semibold text-white truncate max-w-[60%] text-right">
        {value}
      </span>
    </div>
  );
}
